import React, { useEffect, useState } from 'react';
import { SpellCheck, Volume2, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const speak = (text) => {
  if (!window.speechSynthesis) { toast.error('Audio is not supported on this device'); return; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.8;
  window.speechSynthesis.speak(u);
};

const Results = ({ sub }) => (
  <div data-testid="spelling-results">
    <div className="text-2xl font-black text-white mb-3" data-testid="spelling-score">{sub.score}%</div>
    <div className="grid sm:grid-cols-2 gap-1.5">
      {sub.results.map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-sm" data-testid={`spelling-result-${i}`}>
          {r.correct ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
          <span className="font-bold text-white">{r.word}</span>
          {!r.correct && <span className="text-zinc-500 line-through">{r.given || '—'}</span>}
        </div>
      ))}
    </div>
  </div>
);

const SpellingTest = () => {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/school/spelling-test', { headers: authHeaders() }).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data?.eligible) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/school/spelling-test/submit', { week_start: data.week_start, answers }, { headers: authHeaders() });
      setData({ ...data, submission: r.data.submission, items: null });
      toast.success(`Spelling test: ${r.data.submission.score}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-6 border-2 border-sky-500/40 p-5" data-testid="spelling-test">
      <div className="flex items-center gap-2 mb-3">
        <SpellCheck className="w-4 h-4 text-sky-400" />
        <h3 className="text-sm font-black tracking-[0.2em] uppercase">{data.is_test_day ? 'Friday Spelling Test' : "This Week's Spelling Words"}</h3>
      </div>
      {data.submission ? <Results sub={data.submission} /> : data.items ? (
        <div className="space-y-2">
          <p className="text-[11px] text-zinc-400 mb-2">Tap the speaker to hear each word, then type it. Spelling must be exact.</p>
          {data.items.map((it) => (
            <div key={it.idx} className="flex items-center gap-2" data-testid={`spelling-item-${it.idx}`}>
              <span className="text-xs font-black text-zinc-500 w-5">{it.idx + 1}.</span>
              <button onClick={() => speak(it.say)} className="p-2 border-2 border-sky-500/50 hover:border-sky-400 text-sky-300" data-testid={`spelling-play-${it.idx}`}><Volume2 className="w-4 h-4" /></button>
              <input value={answers[it.idx] || ''} onChange={(e) => setAnswers({ ...answers, [it.idx]: e.target.value })} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} className="flex-1 bg-black border-2 border-zinc-800 focus:border-sky-500 outline-none px-3 py-2 text-sm text-white" data-testid={`spelling-input-${it.idx}`} />
            </div>
          ))}
          <button onClick={submit} disabled={busy} className="mt-3 flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-widest disabled:opacity-50" data-testid="spelling-submit-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit Test
          </button>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap gap-2 mb-2">
            {(data.study_words || []).map((w) => <span key={w} className="border-2 border-zinc-800 px-3 py-1 text-sm font-bold text-white" data-testid={`spelling-study-${w}`}>{w}</span>)}
          </div>
          <p className="text-[11px] text-zinc-500">Study these. Your spelling test is on Friday.</p>
        </div>
      )}
    </div>
  );
};

export default SpellingTest;
